'use client'

import { useState } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { cn } from '@paitox/ui/lib/utils'
import { Toaster } from 'sonner'
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  BarChart3,
  CreditCard,
  Gift,
  Package,
  Menu,
  X,
  LogOut,
  Bell,
  ChevronDown,
  Building2,
  Sun,
  Moon,
  ChevronRight
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUI } from '../contexts/UIContext'

const navigation = [
  { name: 'Calendario', href: '/app/calendar', icon: Calendar },
  { name: 'Clientes', href: '/app/clients', icon: Users },
  { name: 'Servicios', href: '/app/services', icon: Package },
  { name: 'Salas', href: '/app/rooms', icon: Building2 },
  { name: 'Staff', href: '/app/staff', icon: Users },
  { name: 'Bonos', href: '/app/packages', icon: Gift },
  { name: 'Gift Cards', href: '/app/gift-cards', icon: CreditCard },
  { name: 'Pagos', href: '/app/payments', icon: CreditCard },
  { name: 'Reportes', href: '/app/reports', icon: BarChart3 },
  { name: 'Configuración', href: '/app/settings', icon: Settings },
]

export function AppLayout() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useUI()
  const location = useLocation()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const initials = user?.name
    ?.split(' ')
    .map((n: string) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'IN'

  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState([
    { id: 1, text: 'Carolina Rojas agendó Masaje Piedras Calientes', time: 'Hace 5 min', unread: true },
    { id: 2, text: 'Nueva solicitud de cita desde el sitio web', time: 'Hace 20 min', unread: true },
    { id: 3, text: 'Recordatorio: Cita de Sofía Morales en 15 minutos', time: 'Hace 1 hora', unread: false }
  ])

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })))
  }

  const unreadCount = notifications.filter(n => n.unread).length

  return (
    <div className="h-screen flex overflow-hidden bg-[hsl(220,18%,96%)]">
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-[hsl(220,24%,17%)] transition-all duration-300 ease-in-out lg:relative lg:translate-x-0',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarCollapsed ? 'w-[68px]' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-white/5 shrink-0">
          <div className={cn('flex items-center gap-3 overflow-hidden', sidebarCollapsed && 'justify-center w-full')}>
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/25">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div className="overflow-hidden">
                <span className="block text-sm font-bold tracking-widest text-white uppercase leading-none">
                  Innovaderm
                </span>
                <span className="block text-[10px] text-slate-400 tracking-wider mt-0.5">
                  Gestión de Spa
                </span>
              </div>
            )}
          </div>
          <button
            className="hidden lg:flex items-center justify-center h-6 w-6 rounded-md text-slate-500 hover:text-slate-300 transition-colors shrink-0"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label="Toggle sidebar"
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform duration-300', !sidebarCollapsed && 'rotate-180')} />
          </button>
          <button
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5 scrollbar-hide">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setMobileSidebarOpen(false)}
                title={sidebarCollapsed ? item.name : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 group relative',
                  isActive
                    ? 'bg-cyan-500/15 text-cyan-400 shadow-[inset_0_0_0_1px_rgba(6,182,212,0.2)]'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-white/5',
                  sidebarCollapsed && 'justify-center px-2'
                )}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full bg-cyan-400" />
                )}
                <item.icon className={cn(
                  'h-4.5 w-4.5 shrink-0 transition-colors',
                  isActive ? 'text-cyan-400' : 'text-slate-500 group-hover:text-slate-300'
                )} />
                {!sidebarCollapsed && (
                  <span className="truncate">{item.name}</span>
                )}
                {/* Tooltip for collapsed state */}
                {sidebarCollapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded-md opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {item.name}
                  </span>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User profile area */}
        <div className="shrink-0 border-t border-white/5 p-3">
          <div
            className={cn(
              'relative flex items-center gap-3 rounded-lg p-2 cursor-pointer hover:bg-white/5 transition-colors',
              sidebarCollapsed && 'justify-center'
            )}
            onClick={() => setProfileOpen(!profileOpen)}
          >
            {/* Avatar */}
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shrink-0 text-white text-xs font-bold shadow-md">
              {initials}
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{user?.name || 'Administrador'}</p>
                  <p className="text-xs text-slate-500 truncate capitalize">{user?.role || 'admin'}</p>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-slate-500 transition-transform', profileOpen && 'rotate-180')} />
              </>
            )}
          </div>

          {/* Profile dropdown */}
          {profileOpen && !sidebarCollapsed && (
            <div className="mt-1 rounded-lg bg-slate-800 border border-white/10 overflow-hidden">
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-rose-400 hover:bg-white/5 transition-colors"
                onClick={() => logout()}
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="h-16 shrink-0 flex items-center gap-3 px-4 lg:px-6 bg-white/80 backdrop-blur-md border-b border-slate-200/60 z-30">
          <button
            className="lg:hidden text-slate-500 hover:text-slate-800 transition-colors"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Breadcrumb / Page title */}
          <div className="flex-1">
            <nav className="flex items-center gap-1.5 text-sm">
              <span className="text-slate-400 font-medium">Innovaderm</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-700 font-semibold capitalize">
                {navigation.find(n => location.pathname.startsWith(n.href))?.name || 'Panel'}
              </span>
            </nav>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 relative">
            <div className="relative">
              <button
                className="relative h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                aria-label="Notificaciones"
              >
                <Bell className="h-4.5 w-4.5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_0_2px_white]" />
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden z-50">
                  <div className="px-4 py-2.5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <span className="text-xs font-bold text-slate-700">Notificaciones ({unreadCount})</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-[10px] font-semibold text-cyan-600 hover:text-cyan-700">
                        Marcar todo leido
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {notifications.map(n => (
                      <div key={n.id} className={cn("p-3 transition-colors hover:bg-slate-50/50", n.unread && "bg-cyan-50/30")}>
                        <p className="text-xs text-slate-700 leading-normal">{n.text}</p>
                        <span className="text-[10px] text-slate-400 mt-1 block">{n.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white text-xs font-bold cursor-pointer">
              {initials}
            </div>
          </div>
        </header>

        {/* Page content — fills the rest of the viewport */}
        <main className="flex-1 overflow-hidden flex flex-col" role="main">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
    </div>
  )
}